import { items } from "@/api/items/useItemQuery";
import { mutateLots } from "@/api/lots/useLotsQuery";
import { customFunctions } from "@/config/customFunction";
import { IncomingInspectionFormValues } from "@/features/inventory/components/form/inspection/IncomingInspectionForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormMain } from "@/features/standard/form/FormMain";
import { LotMutationFromProps } from "@/features/stockPlan/form/StockPlanIncomingForm";
import { LotSelectComplete } from "@/features/ui/autoComplete/lot/lot-\bselectComplete";
import useGetLotsIdQuery from "@/features/ui/autoComplete/lot/select-useGetLotsQuery";
import { LotForm } from "@/features/ui/form/lots/Form";
import { Button, Flex } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { DefaultApiLotsPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EventHandler, useState } from "react";

const LotCreate = Object.assign({}, FormMain, {
  LotSelectComplete: LotSelectComplete,
  Button: Button,
});

interface LotCreateFormProps {
  itemCode: string;
  form: UseFormReturnType<IncomingInspectionFormValues>;
}

export const LotCreateForm = ({ itemCode, form }: LotCreateFormProps) => {
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();

  const [newLot, setNewLot] = useState<LotMutationFromProps>();

  const { data: itemData } = useQuery({
    ...items.codeSearch({
      query: itemCode ? { $and: [{ code: { $eq: itemCode } }] } : "",
    }),
    enabled: !!itemCode,
  });

  const { mutate: postLotsMutate } = useMutation(
    (params: DefaultApiLotsPostRequest) => mutateLots.create(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lots"]);
      },
    },
  );

  const { data: lotData } = useGetLotsIdQuery(
    Number(form.values?.lotId) || Number(newLot?.id),
    !!form.values?.lotId || !!newLot?.id,
  );

  const getLotManagementCriteria = () => {
    if (itemCode && rows?.length) {
      if (usesLotName && usesLotExpiration) return "이름 + 만료일";
      if (usesLotName) return "이름";
      if (usesLotExpiration) return "만료일";
    }
    return "관리 안함";
  };

  const addNewModalHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    openModal(
      <LotForm itemCodeValue={itemCode} onSubmit={onCloseHandler} />,
      null,
      "신규 LOT 등록",
      true,
    );
  };

  const onCloseHandler: EventHandler<any> = (values) => {
    postLotsMutate(
      {
        lotsGetRequest: {
          name: values.name!,
          expiration: values.expiredAt!,
          itemCode: values.itemCode!,
        },
      },
      {
        onSuccess: (data) => {
          setNewLot(data?.data as LotMutationFromProps | undefined);
          form.setFieldValue("lotId", data?.data?.id);
          form.setFieldValue("lotInfo.name", data?.data?.name);
          form.setFieldValue("lotInfo.expiration", data?.data?.expiration);
          alert("로트를 생성했습니다.");
        },
      },
    );
    closeModal(values);
  };

  const rows = itemData?.data?.rows;
  const usesLotName = rows?.[0]?.usesLotName;
  const usesLotExpiration = itemData?.data?.rows?.[0]?.usesLotExpiration;
  const disabled = !(usesLotName || usesLotExpiration);
  const description = `LOT 관리 기준 : ${getLotManagementCriteria()}`;

  return (
    <Flex>
      <LotCreate.LotSelectComplete
        w="100%"
        label="LOT 정보"
        disabled={disabled}
        description={description}
        newIdValue={lotData?.id}
        withAsterisk={customFunctions.ADD_VALIDATION_INCOMING_LOT}
        itemCodeValue={itemCode || lotData?.itemCode || ""}
        dropdownPosition="bottom"
        defaultValue={lotData?.id?.toString() || ""}
        mr="sm"
        styles={{
          description: { fontSize: 10, color: "red" },
          input: lotData ? { fontSize: "0.8em", paddingBottom: "0.5rem" } : undefined,
        }}
        {...form.getInputProps("lotId")}
        onChangeLot={(lot) => {
          form.setFieldValue("lotInfo.name", lot?.label);
          form.setFieldValue("lotInfo.expiration", lot?.expiration);
        }}
      />
      <Flex align="flex-end">
        <LotCreate.Button disabled={disabled} onClick={addNewModalHandler}>
          신규로트 생성
        </LotCreate.Button>
      </Flex>
    </Flex>
  );
};
